import { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
} from "@tanstack/react-table";
import {
  createContext,
  memo,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { useQueryOptions } from "../filters/filters-context";
import { useSupportTickets } from "../hooks/use-support-ticket-data";
import { useSupportTicketTableSelection } from "../hooks/use-support-ticket-table-selection";
import { getSupportTicketsTableColumns } from "./columns/support-tickets-columns";
import { DEFAULT_PAGE_SIZE } from "./types";

interface SupportTicketsTableContextValue {
  data: SupportTicketListItemResponse[];
  columns: ColumnDef<SupportTicketListItemResponse, unknown>[];
  isLoading: boolean;
  currentPage: number;
  totalCount: number;
  pageSize: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  focusedSupportTicketId: SupportTicketId | null;
  focusedSupportTicket: SupportTicketListItemResponse | null;
  setFocusedSupportTicketId: (id: SupportTicketId | null) => void;
  selectedSupportTicketId: SupportTicketId | null;
  openSupportTicket: (id?: SupportTicketId | null) => void;
  navigateUp: () => void;
  navigateDown: () => void;
  actionMenuOpenForId: SupportTicketId | null;
  setActionMenuOpenForId: (id: SupportTicketId | null) => void;
  columnOrder: ColumnOrderState;
  setColumnOrder: (order: ColumnOrderState) => void;
  columnSizing: ColumnSizingState;
  setColumnSizing: (sizing: ColumnSizingState) => void;
  selectedSupportTicketIds: SupportTicketId[];
  selectedSupportTickets: SupportTicketListItemResponse[];
  selectedSupportTicketCount: number;
  hoveredSupportTicketId: SupportTicketId | null;
  hasAnySelectedSupportTickets: boolean;
  setHoveredSupportTicketId: (id: SupportTicketId | null) => void;
  isSupportTicketSelected: (id: SupportTicketId) => boolean;
  setSupportTicketSelected: (input: {
    supportTicketId: SupportTicketId;
    rowIndex: number;
    checked: boolean;
    shiftKey: boolean;
  }) => void;
  setAllSupportTicketsSelected: (checked: boolean) => void;
  clearSelectedSupportTickets: () => void;
}

/**
 * Safely parse a path/query param as a SupportTicketId. Returns null instead of
 * throwing when the value is missing or not a valid `cnv_<base62>` id, so route
 * segments like `/support-v2/analytics` (which fall through to the dynamic
 * `:supportTicketId` segment) don't crash the table provider.
 */
function parseSupportTicketIdOrNull(
  value: string | null | undefined,
): SupportTicketId | null {
  if (!value) {
    return null;
  }
  try {
    return SupportTicketId.make(value);
  } catch {
    return null;
  }
}

const SupportTicketsTableContext =
  createContext<SupportTicketsTableContextValue | null>(null);

export function useSupportTicketsTableContext(): SupportTicketsTableContextValue {
  const context = useContext(SupportTicketsTableContext);
  if (!context) {
    throw new Error(
      "useSupportTicketsTableContext must be used within a SupportTicketsTableProvider",
    );
  }
  return context;
}

export function useSupportTicketsTableContextMaybe(): SupportTicketsTableContextValue | null {
  return useContext(SupportTicketsTableContext);
}

export const SupportTicketsTableProvider = memo(
  function SupportTicketsTableProvider({ children }: { children: ReactNode }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { teamId, supportTicketId: supportTicketIdParam } = useParams<{
      teamId: string;
      supportTicketId: string;
    }>();

    const queryOptions = useQueryOptions();
    const [currentPage, setCurrentPage] = useState(0);
    const [prevQueryOptions, setPrevQueryOptions] = useState(queryOptions);
    if (queryOptions !== prevQueryOptions) {
      setPrevQueryOptions(queryOptions);
      if (currentPage !== 0) {
        setCurrentPage(0);
      }
    }

    const { data, totalCount, isLoading } = useSupportTickets(currentPage);

    const pageSize = DEFAULT_PAGE_SIZE;
    const startIndex = currentPage * pageSize;
    const endIndex = Math.min(startIndex + data.length, totalCount);
    const hasPreviousPage = currentPage > 0;
    const hasNextPage = endIndex < totalCount;

    const goToNextPage = useCallback(() => {
      if (hasNextPage) {
        setCurrentPage((p) => p + 1);
      }
    }, [hasNextPage]);

    const goToPreviousPage = useCallback(() => {
      if (hasPreviousPage) {
        setCurrentPage((p) => p - 1);
      }
    }, [hasPreviousPage]);

    const selectedSupportTicketId =
      parseSupportTicketIdOrNull(supportTicketIdParam);

    const focusedSupportTicketId = useMemo<SupportTicketId | null>(() => {
      if (selectedSupportTicketId) {
        return selectedSupportTicketId;
      }
      const searchParams = new URLSearchParams(location.search);
      return parseSupportTicketIdOrNull(searchParams.get("focused"));
    }, [selectedSupportTicketId, location.search]);

    const setFocusedSupportTicketId = useCallback(
      (id: SupportTicketId | null) => {
        if (selectedSupportTicketId) {
          return;
        }
        const searchParams = new URLSearchParams(location.search);
        if (id) {
          searchParams.set("focused", id);
        } else {
          searchParams.delete("focused");
        }
        const newSearch = searchParams.toString();
        navigate(
          { search: newSearch ? `?${newSearch}` : "" },
          { replace: true },
        );
      },
      [selectedSupportTicketId, location.search, navigate],
    );

    const openSupportTicket = useCallback(
      (id?: SupportTicketId | null) => {
        const targetId = id ?? focusedSupportTicketId;
        if (targetId && teamId) {
          navigate(
            `/stores/${teamId}/support-v2/${targetId}${location.search}`,
          );
        }
      },
      [focusedSupportTicketId, teamId, navigate, location.search],
    );

    const [actionMenuOpenForId, setActionMenuOpenForId] =
      useState<SupportTicketId | null>(null);

    const [columnOrder, setColumnOrder] = useLocalStorage<ColumnOrderState>(
      "redo.support-v2.conversations-table.column-order",
      [],
    );
    const [columnSizing, setColumnSizing] = useLocalStorage<ColumnSizingState>(
      "redo.support-v2.conversations-table.column-sizing",
      {},
    );

    const columns = useMemo(
      () => getSupportTicketsTableColumns({ enablePinning: true }),
      [],
    );

    const {
      selectedSupportTicketIds,
      selectedSupportTickets,
      selectedSupportTicketCount,
      hoveredSupportTicketId,
      hasAnySelectedSupportTickets,
      setHoveredSupportTicketId,
      isSupportTicketSelected,
      setSupportTicketSelected,
      setAllSupportTicketsSelected,
      clearSelectedSupportTickets,
    } = useSupportTicketTableSelection(data);

    const focusedSupportTicket = useMemo(() => {
      if (!focusedSupportTicketId) {
        return null;
      }
      return data.find((c) => c.id === focusedSupportTicketId) ?? null;
    }, [data, focusedSupportTicketId]);

    const navigateUp = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedSupportTicketId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setFocusedSupportTicketId(data[data.length - 1].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedSupportTicketId,
      );
      if (currentIndex <= 0) {
        return;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFocusedSupportTicketId(data[currentIndex - 1].id);
    }, [data, focusedSupportTicketId, setFocusedSupportTicketId]);

    const navigateDown = useCallback(() => {
      if (data.length === 0) {
        return;
      }
      if (!focusedSupportTicketId) {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        setFocusedSupportTicketId(data[0].id);
        return;
      }
      const currentIndex = data.findIndex(
        (c) => c.id === focusedSupportTicketId,
      );
      if (currentIndex === -1 || currentIndex >= data.length - 1) {
        return;
      }
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      setFocusedSupportTicketId(data[currentIndex + 1].id);
    }, [data, focusedSupportTicketId, setFocusedSupportTicketId]);

    const value = useMemo<SupportTicketsTableContextValue>(
      () => ({
        data,
        columns,
        isLoading,
        currentPage,
        totalCount,
        pageSize,
        hasPreviousPage,
        hasNextPage,
        goToNextPage,
        goToPreviousPage,
        focusedSupportTicketId,
        focusedSupportTicket,
        setFocusedSupportTicketId,
        selectedSupportTicketId,
        openSupportTicket,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
        setActionMenuOpenForId,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        selectedSupportTicketIds,
        selectedSupportTickets,
        selectedSupportTicketCount,
        hoveredSupportTicketId,
        hasAnySelectedSupportTickets,
        setHoveredSupportTicketId,
        isSupportTicketSelected,
        setSupportTicketSelected,
        setAllSupportTicketsSelected,
        clearSelectedSupportTickets,
      }),
      [
        data,
        columns,
        isLoading,
        currentPage,
        totalCount,
        pageSize,
        hasPreviousPage,
        hasNextPage,
        goToNextPage,
        goToPreviousPage,
        focusedSupportTicketId,
        focusedSupportTicket,
        setFocusedSupportTicketId,
        selectedSupportTicketId,
        openSupportTicket,
        navigateUp,
        navigateDown,
        actionMenuOpenForId,
        columnOrder,
        setColumnOrder,
        columnSizing,
        setColumnSizing,
        selectedSupportTicketIds,
        selectedSupportTickets,
        selectedSupportTicketCount,
        hoveredSupportTicketId,
        hasAnySelectedSupportTickets,
        setHoveredSupportTicketId,
        isSupportTicketSelected,
        setSupportTicketSelected,
        setAllSupportTicketsSelected,
        clearSelectedSupportTickets,
      ],
    );

    return (
      <SupportTicketsTableContext.Provider value={value}>
        {children}
      </SupportTicketsTableContext.Provider>
    );
  },
);
